import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { BrandEnum, i18n, useGetTermsAndConditionsForOffer } from 'src/common';
import { CheckboxInput, Flex, Text, Card } from 'src/components';
import { FieldConfig, Form, FormRef } from './form';
import { Validation } from './validation';
import styled from '@emotion/styled';
import { colors } from 'src/theme';

export interface DataConsentFormValues {
  validDataConsent: boolean;
  fourteenDayCancellationPolicy: boolean;
  uniqaDistanceCommunicationConsent?: boolean;
}

export const INITIAL_DATA_CONSENT_VALUES: DataConsentFormValues = {
  validDataConsent: false,
  fourteenDayCancellationPolicy: false,
  uniqaDistanceCommunicationConsent: false,
};

const TermsLink = styled.a({
  color: colors.primary,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontWeight: 600,
  '&:hover': {
    opacity: 0.8,
  },
});

interface DataConsentFormProps {
  data?: Partial<DataConsentFormValues>;
  onSubmit?: (data: DataConsentFormValues) => void;
  offerId?: string;
  brandCode?: string;
}

export const DataConsentForm = forwardRef<FormRef, DataConsentFormProps>(
  ({ data, onSubmit, brandCode, offerId }, ref) => {
    const formRef = React.useRef<FormRef>(null);
    const isUniqa = brandCode === BrandEnum.UNIQA;
    const { termsAndConditions } = useGetTermsAndConditionsForOffer(offerId);
    const termsUrl = termsAndConditions?.documents?.find(doc => doc.src.includes("Opći uvjeti za osiguranje imovine"))?.src;

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formRef.current) {
          formRef.current.submitForm();
        }
      },
      getValues: () => formRef.current?.getValues?.() || {},
      validateForm: () => formRef.current?.validateForm?.() ?? false,
      setFieldValue: (fieldName: string, value: unknown, validation?: boolean) => {
        formRef.current?.setFieldValue?.(fieldName, value, validation);
      },
    }));

    const handleTermsClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    const fields: FieldConfig<DataConsentFormValues>[] = useMemo(() => {
      const baseFields: FieldConfig<DataConsentFormValues>[] = [
        {
          name: 'validDataConsent',
          component: CheckboxInput,
          componentProps: {
            left: true,
            labelComponent: (
              <Flex vertical gap={12}>
                <Text type="small">
                  {i18n.t('pages.offerConfirmation.validDataConsent')}
                </Text>
                <Text type="small">
                  {i18n.t('pages.offerConfirmation.generalTOSConsent')}
                </Text>
              </Flex>
            ),
          },
          validations: [
            {
              type: 'requiredTrue',
              message: i18n.t('pages.offerConfirmation.validDataConsentRequired'),
            },
          ] as Validation[],
        }
      ];

      if (isUniqa) {
        baseFields.push({
          name: 'uniqaDistanceCommunicationConsent',
          component: CheckboxInput,
          componentProps: {
            left: true,
            labelComponent: (
              <Text type="small">
                {i18n.t('pages.offerConfirmation.uniqaDistanceCommunicationConsentPrefix')}
                {termsUrl ? (
                  <TermsLink
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleTermsClick}
                  >
                    {i18n.t('pages.offerConfirmation.uniqaDistanceCommunicationConsentLink')}
                  </TermsLink>
                ) : (
                  <strong>{i18n.t('pages.offerConfirmation.uniqaDistanceCommunicationConsentLink')}</strong>
                )}
                {i18n.t('pages.offerConfirmation.uniqaDistanceCommunicationConsentSuffix')}
              </Text>
            ),
          },
          validations: [
            {
              type: 'requiredTrue',
              message: i18n.t('pages.offerConfirmation.uniqaDistanceCommunicationConsentRequired'),
            },
          ] as Validation[],
        });
      }

      return baseFields;
    }, [isUniqa, termsUrl]);

    const handleSubmit = (values: Record<string, unknown>) => {
      const raw = values as Partial<DataConsentFormValues>;
      onSubmit?.({ ...INITIAL_DATA_CONSENT_VALUES, ...raw } as DataConsentFormValues);
    };

    return (
      <Card
        padding={16}
        style={{
          backgroundColor: 'transparent',
          borderRadius: '8px',
          border: '1px dashed var(--Text-Text-4, #999)',
          paddingBottom: '32px'
        }}
      >
        <Form
          ref={formRef}
          fields={fields}
          initialValues={{ ...INITIAL_DATA_CONSENT_VALUES, ...data }}
          onSubmit={handleSubmit}
          config={{ gap: 24 }}
        />
      </Card>
    );
  }
);